import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, ArrowLeft, FileText, Image as ImageIcon, X } from 'lucide-react';
import axios from 'axios';
import PexelsImagePicker from '../../portal/components/PexelsImagePicker';
import { pexelsifyUrl } from '../../utils/pexels';

const API = process.env.REACT_APP_BACKEND_URL;

function GuideForm({ id }) {
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', description: '', slug: '', file_id: '', cover_image_id: '', cover_image_url: '', destination_tag: '', status: 'draft' });
  const [saving, setSaving] = useState(false);
  const [mediaModal, setMediaModal] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [pickMode, setPickMode] = useState('pdf'); // 'pdf' or 'image'
  // Feb 2026 — Pexels picker + paste-URL for cover image.
  const [pexelsOpen, setPexelsOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  useEffect(() => {
    if (!isNew) {
      axios.get(`${API}/api/cms/guides/${id}`, { withCredentials: true })
        .then(res => setForm(res.data))
        .catch(() => navigate('/advisor/content/guides'));
    }
  }, [id, isNew, navigate]);

  const autoSlug = (t) => t.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-');
  const update = (field, val) => {
    setForm(f => {
      const next = { ...f, [field]: val };
      if (field === 'title' && (isNew || f.slug === autoSlug(f.title))) next.slug = autoSlug(val);
      return next;
    });
  };

  const save = async (status) => {
    setSaving(true);
    const body = { ...form, status: status || form.status };
    try {
      if (isNew) {
        await axios.post(`${API}/api/cms/guides`, body, { withCredentials: true });
      } else {
        await axios.put(`${API}/api/cms/guides/${id}`, body, { withCredentials: true });
      }
      navigate('/advisor/content/guides');
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const openPicker = (mode) => {
    setPickMode(mode);
    setMediaModal(true);
    const type = mode === 'pdf' ? 'pdf' : 'image';
    axios.get(`${API}/api/cms/media?media_type=${type}`, { withCredentials: true }).then(res => setMediaItems(res.data));
  };

  const selectMedia = (item) => {
    if (pickMode === 'pdf') update('file_id', item.id);
    else {
      update('cover_image_id', item.id);
      update('cover_image_url', '');
    }
    setMediaModal(false);
  };

  // Feb 2026 — Auto-import any external image URL into the Image House.
  // Fire-and-forget so the cover reflects immediately even if the import
  // takes a moment. Tagged with the guide's destination so the Media
  // Library stays organised.
  const autoSaveImageToHouse = useCallback(async (rawUrl, opts = {}) => {
    const url = (rawUrl || '').trim();
    if (!url || !/^https?:\/\//i.test(url)) return null;
    try {
      const { data } = await axios.post(
        `${API}/api/cms/media/import-url`,
        {
          url,
          alt_text: opts.alt || form.title || '',
          destination_tag: form.destination_tag || '',
          source: opts.source || 'guide-editor',
          tags: [form.destination_tag].filter(Boolean),
        },
        { withCredentials: true },
      );
      if (data?.id) {
        // Switch the cover to the imported Media doc so the file is
        // permanently in the Image House (rather than relying on the
        // external CDN URL forever).
        update('cover_image_id', data.id);
        update('cover_image_url', '');
      }
      return data;
    } catch (err) {
      console.warn('[image-house] guide import failed', err?.response?.data || err?.message || err);
      return null;
    }
  }, [form.title, form.destination_tag]);

  const applyExternalCover = (rawUrl) => {
    let url = (rawUrl || '').trim();
    if (!url) return;
    // Auto-convert Pexels page URLs to the direct CDN URL.
    const converted = pexelsifyUrl(url);
    if (converted) url = converted;
    update('cover_image_url', url);
    update('cover_image_id', '');
    autoSaveImageToHouse(url, { source: 'guide-cover-url' });
    setUrlInput('');
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="guide-editor">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between mb-8">
          <Link to="/advisor/content/guides" className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#999] hover:text-[#1A1A1A]">
            <ArrowLeft size={12} /> Guides
          </Link>
          <div className="flex gap-2">
            <button onClick={() => save('draft')} disabled={saving} className="px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A]" data-testid="guide-save-draft">
              <Save size={12} className="inline mr-1" />Save Draft
            </button>
            <button onClick={() => save('published')} disabled={saving} className="px-4 py-2 text-sm bg-[#1A1A1A] text-white rounded hover:bg-[#333]" data-testid="guide-publish">
              <Eye size={12} className="inline mr-1" />Publish
            </button>
          </div>
        </div>

        <div className="space-y-5">
          <input value={form.title} onChange={e => update('title', e.target.value)} placeholder="Guide title" className="w-full font-serif text-2xl bg-transparent border-none outline-none placeholder:text-[#E8E4DC]" data-testid="guide-title-input" />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.slug} onChange={e => update('slug', e.target.value)} placeholder="slug" className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none" data-testid="guide-slug-input" />
            <input value={form.destination_tag} onChange={e => update('destination_tag', e.target.value)} placeholder="Destination tag" className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none" data-testid="guide-dest-input" />
          </div>
          <textarea value={form.description} onChange={e => update('description', e.target.value)} placeholder="Description" rows={3} className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none resize-none" data-testid="guide-desc-input" />
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <button onClick={() => openPicker('pdf')} className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white hover:border-[#999]" data-testid="guide-pdf-btn">
                {form.file_id ? 'PDF attached' : 'Attach PDF'}
              </button>
              <button onClick={() => openPicker('image')} className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white hover:border-[#999]" data-testid="guide-cover-btn">
                Image House
              </button>
              <button onClick={() => setPexelsOpen(true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white hover:border-[#999]" data-testid="guide-cover-pexels">
                <ImageIcon size={12} /> Pick from Pexels
              </button>
            </div>
            <div className="flex gap-2">
              <input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyExternalCover(urlInput); } }}
                placeholder="…or paste an image URL (auto-saved to Image House)"
                className="flex-1 text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none focus:border-[#1A1A1A]"
                data-testid="guide-cover-url-input"
              />
              <button
                type="button"
                onClick={() => applyExternalCover(urlInput)}
                disabled={!urlInput.trim()}
                className="text-xs px-3 py-2 bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-40"
                data-testid="guide-cover-url-apply"
              >
                Set as cover
              </button>
            </div>
            <p className="mt-1 text-[11px] text-[#999]">
              Every image you select is saved to the Image House{form.destination_tag ? `, organised under ${form.destination_tag}` : ''}.
            </p>
          </div>
          {(form.cover_image_id || form.cover_image_url) && (
            <div className="relative">
              <img
                src={form.cover_image_id ? `${API}/api/cms/media/file/${form.cover_image_id}` : form.cover_image_url}
                alt="Cover"
                className="w-full h-40 object-cover rounded-lg"
                data-testid="guide-cover-preview"
              />
              <button
                type="button"
                onClick={() => { update('cover_image_id', ''); update('cover_image_url', ''); }}
                className="absolute top-2 right-2 inline-flex items-center justify-center size-7 rounded-full bg-black/55 hover:bg-black/75 text-white"
                title="Remove cover"
                data-testid="guide-cover-clear"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      {mediaModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-6" onClick={() => setMediaModal(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[70vh] overflow-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg mb-4">Select {pickMode === 'pdf' ? 'PDF' : 'Image'}</h3>
            {mediaItems.length === 0 ? (
              <p className="text-sm text-[#999]">No files. <Link to="/advisor/content/media" className="underline">Upload media first.</Link></p>
            ) : pickMode === 'pdf' ? (
              <div className="space-y-2">
                {mediaItems.map(item => (
                  <button key={item.id} onClick={() => selectMedia(item)} className="w-full flex items-center gap-3 p-3 border border-[#E8E4DC] rounded hover:border-[#1A1A1A] text-left">
                    <FileText size={16} className="text-[#999]" />
                    <span className="text-sm">{item.original_filename}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {mediaItems.map(item => (
                  <button key={item.id} onClick={() => selectMedia(item)} className="aspect-square overflow-hidden rounded border border-[#E8E4DC] hover:border-[#1A1A1A]">
                    <img src={`${API}${item.file_url}`} alt={item.original_filename} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feb 2026 — Pexels picker.  Picked photo URL auto-imports into
          the Image House under the guide's destination tag. */}
      <PexelsImagePicker
        open={pexelsOpen}
        defaultQuery={form.destination_tag || form.title || ''}
        onClose={() => setPexelsOpen(false)}
        onPick={(photo) => {
          setPexelsOpen(false);
          const url = photo?.url || '';
          if (!url) return;
          update('cover_image_url', url);
          update('cover_image_id', '');
          autoSaveImageToHouse(url, {
            alt: photo?.photographer ? `Photograph by ${photo.photographer} on Pexels` : '',
            source: 'guide-pexels-picker',
          });
        }}
      />
    </div>
  );
}

export default function GuidesCMS() {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      axios.get(`${API}/api/cms/guides`, { withCredentials: true })
        .then(res => { setItems(res.data); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [id]);

  const handleDelete = async (gid) => {
    if (!window.confirm('Delete this guide?')) return;
    await axios.delete(`${API}/api/cms/guides/${gid}`, { withCredentials: true });
    setItems(prev => prev.filter(i => i.id !== gid));
  };

  if (id) return <GuideForm id={id} />;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="guides-cms">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-start justify-between mb-10">
          <div>
            <Link to="/advisor/content" className="text-xs uppercase tracking-[0.15em] text-[#999] hover:text-[#1A1A1A]">Content</Link>
            <h1 className="font-serif text-3xl text-[#1A1A1A] mt-4">Travel Guides</h1>
          </div>
          <button onClick={() => navigate('/advisor/content/guides/new')} className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333]" data-testid="guide-new-btn">
            <Plus size={14} /> New Guide
          </button>
        </div>

        {loading ? <p className="text-sm text-[#999]">Loading...</p> : items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#E8E4DC] rounded-lg">
            <p className="text-sm text-[#999]">No guides yet</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="guides-list">
            {items.map(item => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-white border border-[#E8E4DC] rounded-lg group" data-testid={`guide-item-${item.id}`}>
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-3 mb-0.5">
                    <h3 className="font-serif text-base text-[#1A1A1A]">{item.title}</h3>
                    {item.status === 'published' ? (
                      <span className="text-[10px] text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Published</span>
                    ) : (
                      <span className="text-[10px] text-[#999] bg-[#F5F3EE] px-2 py-0.5 rounded-full">Draft</span>
                    )}
                  </div>
                  <p className="text-xs text-[#999]">{item.description}</p>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => navigate(`/advisor/content/guides/${item.id}`)} className="p-2 text-[#6B6B6B] hover:text-[#1A1A1A]"><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 text-[#6B6B6B] hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
